import React, { useState, useEffect } from 'react';
import { ShieldCheck, Cookie } from 'lucide-react';

export interface CookieConsent {
  analytics: boolean;
  at: number;
}

interface CookieBannerProps {
  onOpenPrivacy?: () => void;
}

export const COOKIE_CONSENT_KEY = 'plotwise_consent';

export function getCookieConsent(): CookieConsent | null {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.analytics === 'boolean') {
      return parsed as CookieConsent;
    }
  } catch {
    // ignore parsing error
  }
  return null;
}

export function setCookieConsent(analytics: boolean): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
  try {
    const consent: CookieConsent = { analytics, at: Date.now() };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
  } catch {
    // ignore
  }
}

export default function CookieBanner({ onOpenPrivacy }: CookieBannerProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Vis kun banneret hvis brugeren ikke allerede har taget stilling
    const existing = getCookieConsent();
    if (!existing) {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const handleAcceptNecessary = () => {
    setCookieConsent(false);
    setVisible(false);
  };

  const handleAcceptAll = () => {
    setCookieConsent(true);
    setVisible(false);
  };

  return (
    <div
      className="cookie-banner cookie-banner-backdrop"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 'var(--z-popover)',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        overflowY: 'auto',
      }}
    >
      <section
        className="cookie-banner-container"
        role="dialog"
        aria-modal="true"
        aria-label="Cookie samtykke"
        style={{
          width: '100%',
          maxWidth: '460px',
          margin: 'auto',
          boxSizing: 'border-box',
          backgroundColor: '#0f1b14',
          border: '1px solid rgba(74, 222, 128, 0.3)',
          borderRadius: '16px',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.6)',
          padding: '22px 20px',
          color: '#e2e8f0',
          fontFamily: 'inherit',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '18px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
            }}
          >
            <div
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '10px',
                backgroundColor: 'rgba(74, 222, 128, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#4ade80',
                flexShrink: 0,
              }}
            >
              <Cookie size={20} />
            </div>
            <div style={{ fontSize: '0.86rem', lineHeight: '1.45', color: '#cbd5e1' }}>
              <strong style={{ color: '#ffffff', display: 'block', marginBottom: '2px' }}>
                Vi respekterer dit privatliv
              </strong>
              Plotwise anvender nødvendige cookies til at sikre login og revirfunktioner. Med dit
              samtykke anvender vi anonymiseret statistik for at forbedre hastighed og kortvisning.{' '}
              {onOpenPrivacy && (
                <button
                  type="button"
                  onClick={onOpenPrivacy}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: '#4ade80',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontSize: 'inherit',
                    display: 'inline',
                  }}
                >
                  Læs privatlivspolitik (GDPR)
                </button>
              )}
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
              justifyContent: 'stretch',
            }}
          >
            <button
              type="button"
              onClick={handleAcceptNecessary}
              style={{
                minHeight: '48px',
                flex: '1 1 140px',
                padding: '0 16px',
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.16)',
                color: '#e2e8f0',
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              Kun nødvendige
            </button>
            <button
              type="button"
              onClick={handleAcceptAll}
              style={{
                minHeight: '48px',
                flex: '1 1 140px',
                justifyContent: 'center',
                padding: '0 18px',
                borderRadius: '8px',
                backgroundColor: '#15803d',
                border: '1px solid #22c55e',
                color: '#ffffff',
                fontSize: '0.85rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 2px 10px rgba(22, 163, 74, 0.35)',
                transition: 'all 0.15s ease',
              }}
            >
              <ShieldCheck size={16} />
              Accepter alle
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
